import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailDropdownTriggerClass,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import {
  DOCUMENT_MODULE_OPTIONS,
  getEntryPointOptionsForModule,
  FILE_FORMAT_OPTIONS,
  EXPIRY_REMINDER_TIMING_OPTIONS,
  REPLACEMENT_RULE_OPTIONS,
  FAILED_DOCUMENT_RESULT_OPTIONS,
  EXPIRED_DOCUMENT_RESULT_OPTIONS,
  MISSING_DOCUMENT_RESULT_OPTIONS,
  INVALID_DOCUMENT_RESULT_OPTIONS,
  buildSampleDocumentRules,
  normalizeDocumentRuleForForm,
  findDuplicateDocumentRule,
  mergeDocumentRuleForList,
  nextDocumentRuleId,
} from '../../../data/documentsConfig';
import { getConfigurationRuleTestPath } from '../../../data/configurationTestSimulationConfig';
import ConfigurationSaveTestModal from './shared/ConfigurationSaveTestModal';
import ConfigurationRuleTestResultPanel from './shared/ConfigurationRuleTestResultPanel';
import { matchesUrlName, urlNameSegment } from '../../../utils/slugify';

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const ToggleRow = ({ label, checked, onChange, disabled, required = false, isView = false }) => {
  if (isView) {
    return <TextValue label={label} value={checked ? 'Yes' : 'No'} />;
  }
  return (
    <div className="flex items-center gap-3 flex-wrap w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required && <span className="text-alertred">*</span>}
      </AdminDetailFieldLabel>
      <label className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" disabled={disabled} />
        <div className="relative w-9 h-5 bg-lightgray rounded-full peer-checked:bg-primaryblue after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4 peer-disabled:opacity-50" />
        <span className="ml-2 text-sm text-offblack">{checked ? 'Yes' : 'No'}</span>
      </label>
    </div>
  );
};

const MultiCheckboxDropdown = ({ label, options, values, onToggle, disabled, error, placeholder, required = false }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  useClickOutside(ref, () => setOpen(false));
  const summary = values.length ? values.map((v) => options.find((o) => o.value === v)?.label || v).join(', ') : '';
  const triggerClass = adminFormDetailDropdownTriggerClass(disabled, Boolean(error));
  if (disabled) {
    return <TextValue label={label} value={summary || '—'} />;
  }
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required && <span className="text-alertred">*</span>}
      </AdminDetailFieldLabel>
      <div className="relative w-full min-w-0" ref={ref}>
        <button type="button" onClick={() => setOpen(!open)} className={triggerClass}>
          <span title={summary || placeholder} className={`min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap !text-sm ${summary ? 'text-offblack' : 'text-mediumgray'}`}>
            {summary || placeholder}
          </span>
        </button>
        <AdminFormDropdownChevron open={open} disabled={disabled} />
        {open && (
          <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-[220px] overflow-y-auto rounded border border-lightgray bg-white shadow-lg py-2 px-2">
            {options.map((o) => (
              <label key={o.value} className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded">
                <input type="checkbox" className="rounded border-lightgray" checked={values.includes(o.value)} onChange={() => onToggle(o.value)} />
                <span className="text-sm text-offblack">{o.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      {error && <AdminFormFieldError>{error}</AdminFormFieldError>}
    </div>
  );
};

const emptyForm = () => ({
  ruleName: '',
  ruleArea: '',
  uploadAllowed: true,
  entryPoint: '',
  allowedFileFormats: [],
  maximumFileSize: '',
  maximumFileCount: '',
  multipleUploadAllowed: false,
  checkRequired: true,
  checkOutcomeRequired: false,
  moreInformationAllowed: false,
  failedDocumentResult: '',
  expiryApplies: false,
  expiryReminderTiming: '',
  replacementAllowed: false,
  replacementMethod: '',
  expiredDocumentResult: '',
  missingDocumentResult: '',
  invalidDocumentResult: '',
  createdOn: '',
  createdBy: '',
  modifiedOn: '',
  modifiedBy: '',
});

const listRowToForm = (rule) => {
  if (!rule) return emptyForm();
  const normalized = normalizeDocumentRuleForForm(rule);
  const base = emptyForm();
  return {
    ...base,
    ...normalized,
    allowedFileFormats: Array.isArray(normalized.allowedFileFormats) ? normalized.allowedFileFormats : [],
  };
};

const DocumentRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const isAdd = !ruleName || ruleName === 'add';
  const isEdit = location.pathname.includes('/edit');
  const isView = !isAdd && !isEdit;

  const existingRules = useMemo(
    () => (Array.isArray(location.state?.rules) ? location.state.rules : buildSampleDocumentRules()),
    [location.state?.rules],
  );

  const initial = useMemo(() => {
    if (location.state?.rule) return listRowToForm(location.state.rule);
    if (!isAdd && ruleName) {
      const found = existingRules.find((r) => matchesUrlName(r.ruleName, ruleName));
      if (found) return listRowToForm(found);
    }
    return emptyForm();
  }, [isAdd, ruleName, location.state, existingRules]);

  const [formData, setFormData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saveTestModal, setSaveTestModal] = useState(null);
  const [testResultFeedback, setTestResultFeedback] = useState(null);

  useEffect(() => {
    setFormData(initial);
  }, [initial]);

  useEffect(() => {
    const testResult = location.state?.testResult;
    if (!testResult) return;
    setTestResultFeedback(testResult);
    const { testResult: _removed, ...rest } = location.state || {};
    navigate(location.pathname, { replace: true, state: rest });
  }, [location.state, location.pathname, navigate]);

  const entryPointOptions = useMemo(() => getEntryPointOptionsForModule(formData.ruleArea), [formData.ruleArea]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'ruleArea') {
        next.entryPoint = '';
      }
      return next;
    });
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    if (errors.form) setErrors((prev) => { const n = { ...prev }; delete n.form; return n; });
  };

  const toggleFormat = (value) => {
    setFormData((prev) => {
      const arr = prev.allowedFileFormats || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, allowedFileFormats: next };
    });
    if (errors.allowedFileFormats) setErrors((prev) => { const n = { ...prev }; delete n.allowedFileFormats; return n; });
  };

  const validate = () => {
    const next = {};
    if (!formData.ruleName?.trim()) next.ruleName = 'Enter a rule name.';
    const excludeDupId = isAdd ? undefined : formData.id;
    if (formData.ruleArea && formData.entryPoint) {
      const dup = findDuplicateDocumentRule(existingRules, formData.ruleArea, formData.entryPoint, excludeDupId);
      if (dup) next.form = `A document rule already exists for this module and entry point (${dup.id}).`;
    }

    if (!formData.ruleArea) next.ruleArea = 'Select a module.';
    if (!formData.entryPoint) next.entryPoint = 'Select an entry point.';
    if (entryPointOptions.length && !entryPointOptions.some((o) => o.value === formData.entryPoint)) {
      next.entryPoint = 'Select a valid entry point for this module.';
    }
    if (formData.uploadAllowed) {
      if (!(formData.allowedFileFormats || []).length) next.allowedFileFormats = 'Select at least one file format.';
      if (!String(formData.maximumFileSize || '').trim()) next.maximumFileSize = 'Enter maximum file size.';
      if (!String(formData.maximumFileCount || '').trim()) next.maximumFileCount = 'Enter maximum file count.';
    }
    if (!formData.missingDocumentResult) next.missingDocumentResult = 'Select missing document result.';
    if (formData.expiryApplies && !formData.expiryReminderTiming) next.expiryReminderTiming = 'Select expiry reminder timing.';
    if (formData.replacementAllowed && !formData.replacementMethod) next.replacementMethod = 'Select replacement rule.';
    if (formData.expiryApplies && !formData.expiredDocumentResult) next.expiredDocumentResult = 'Select expired document result.';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const nowIso = new Date().toISOString();
    const actor = 'System Admin';
    const id = isAdd ? nextDocumentRuleId(existingRules) : formData.id;
    const record = mergeDocumentRuleForList(formData, {
      id,
      status: 'active',
      actor,
      nowIso,
      isNew: isAdd,
    });
    setSaveTestModal({ phase: 'confirm', record });
  };

  const handleSaveWithoutTest = () => {
    if (!saveTestModal?.record) return;
    const record = saveTestModal.record;
    const merged = [...existingRules.filter((r) => r.id !== record.id), record];
    setSaveTestModal(null);
    navigate('/admin/configuration/documents', { state: { rules: merged } });
  };

  const handleRunSaveTest = () => {
    if (!saveTestModal?.record) return;
    const record = saveTestModal.record;
    setSaveTestModal(null);
    navigate(getConfigurationRuleTestPath('documents', urlNameSegment(record.ruleName)), {
      state: {
        rule: record,
        existingRules,
        returnPath: `/admin/configuration/documents/${urlNameSegment(record.ruleName)}/edit`,
      },
    });
  };

  const showRuleId = Boolean(!isAdd && ruleName && ruleName !== 'add');

  const renderInput = (name, label, placeholder, required = true, inputProps = {}) => {
    if (isView) {
      return <TextValue label={label} value={formData[name]} />;
    }
    return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required && <span className="text-alertred">*</span>}
      </AdminDetailFieldLabel>
      <input
        type="text"
        placeholder={placeholder}
        value={formData[name]}
        onChange={(e) => handleChange(name, e.target.value)}
        className={adminFormDetailInputClasses(false, Boolean(errors[name]))}
        {...inputProps}
      />
      {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
    </div>
    );
  };

  const renderSelectField = (name, label, options, { placeholder, disabled = false, required = true } = {}) => {
    if (isView) {
      const selected = options.find((o) => o.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required && <span className="text-alertred">*</span>}
      </AdminDetailFieldLabel>
      <div className="relative w-full min-w-0">
        <select
          id={`document-rule-select-${name}`}
          value={formData[name] || ''}
          title={options.find((o) => o.value === formData[name])?.label || placeholder}
          onChange={(e) => handleChange(name, e.target.value)}
          disabled={disabled}
          className={adminFormDetailSelectClasses(disabled, Boolean(errors[name]))}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <AdminFormDropdownChevron />
      </div>
      {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
    </div>
    );
  };

  if (isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Document Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Rule Setup</AdminPanelTitle>
              {showRuleId ? (
                <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView />
              ) : null}
              <div className={adminFormTwoColGridClass}>
                {renderInput('ruleName', 'Rule Name', 'Enter rule name')}
                {renderSelectField('ruleArea', 'Module', DOCUMENT_MODULE_OPTIONS, { placeholder: 'Select module' })}
              </div>
              <div className={adminFormTwoColGridClass}>
                {renderSelectField('entryPoint', 'Entry Point', entryPointOptions, {
                  placeholder: formData.ruleArea ? 'Select entry point' : 'Select module first',
                  disabled: !formData.ruleArea,
                })}
              </div>
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Upload Rules</AdminPanelTitle>
              <ToggleRow isView label="Upload Allowed" required checked={formData.uploadAllowed} onChange={(v) => handleChange('uploadAllowed', v)} disabled />
              {formData.uploadAllowed ? (
                <>
                  <MultiCheckboxDropdown
                    label="Allowed File Formats"
                    required
                    options={FILE_FORMAT_OPTIONS}
                    values={formData.allowedFileFormats}
                    onToggle={(v) => toggleFormat(v)}
                    disabled
                    error={errors.allowedFileFormats}
                    placeholder="Select formats"
                  />
                  <div className={adminFormTwoColGridClass}>
                    <TextValue
                      label="Maximum File Size"
                      value={formData.maximumFileSize ? `${formData.maximumFileSize} MB` : '—'}
                    />
                    <TextValue label="Maximum File Count" value={formData.maximumFileCount} />
                  </div>
                  <ToggleRow isView label="Multiple Upload Allowed" required checked={formData.multipleUploadAllowed} onChange={(v) => handleChange('multipleUploadAllowed', v)} disabled />
                </>
              ) : null}
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Check Rules</AdminPanelTitle>
              <ToggleRow isView label="Check Required" required checked={formData.checkRequired} onChange={(v) => handleChange('checkRequired', v)} disabled />
              <ToggleRow isView label="Check Outcome Required" required checked={formData.checkOutcomeRequired} onChange={(v) => handleChange('checkOutcomeRequired', v)} disabled />
              <ToggleRow isView label="More Information Allowed" required checked={formData.moreInformationAllowed} onChange={(v) => handleChange('moreInformationAllowed', v)} disabled />
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Expiry & Replacement</AdminPanelTitle>
              <ToggleRow
                isView
                label="Expiry Applies"
                required
                checked={formData.expiryApplies}
                disabled
                onChange={(v) => {
                  setFormData((prev) => ({
                    ...prev,
                    expiryApplies: v,
                    expiryReminderTiming: v ? prev.expiryReminderTiming : '',
                    expiredDocumentResult: v ? prev.expiredDocumentResult : '',
                  }));
                  setErrors((prev) => {
                    const n = { ...prev };
                    if (!v) {
                      delete n.expiryReminderTiming;
                      delete n.expiredDocumentResult;
                    }
                    return n;
                  });
                }}
              />
              {formData.expiryApplies ? (
                <div className={adminFormTwoColGridClass}>
                  {renderSelectField('expiryReminderTiming', 'Expiry Reminder Timing', EXPIRY_REMINDER_TIMING_OPTIONS, {
                    placeholder: 'Select timing',
                  })}
                </div>
              ) : null}
              <ToggleRow
                isView
                label="Replacement Allowed"
                required
                checked={formData.replacementAllowed}
                disabled
                onChange={(v) => {
                  setFormData((prev) => ({
                    ...prev,
                    replacementAllowed: v,
                    replacementMethod: v ? prev.replacementMethod : '',
                  }));
                  if (errors.replacementMethod) {
                    setErrors((prev) => {
                      const n = { ...prev };
                      delete n.replacementMethod;
                      return n;
                    });
                  }
                }}
              />
              {formData.replacementAllowed ? (
                <div className={adminFormTwoColGridClass}>
                  {renderSelectField('replacementMethod', 'Replacement Rule', REPLACEMENT_RULE_OPTIONS, {
                    placeholder: 'Select replacement rule',
                  })}
                </div>
              ) : null}
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Document Result</AdminPanelTitle>
              <div className={adminFormTwoColGridClass}>
                {renderSelectField('missingDocumentResult', 'Missing Document Result', MISSING_DOCUMENT_RESULT_OPTIONS, {
                  placeholder: 'Select result',
                })}
                {renderSelectField(
                  'invalidDocumentResult',
                  'Invalid Document Result',
                  [{ value: '', label: '—' }, ...INVALID_DOCUMENT_RESULT_OPTIONS],
                  { placeholder: 'Select result', required: false },
                )}
                {renderSelectField(
                  'failedDocumentResult',
                  'Failed Document Result',
                  [{ value: '', label: '—' }, ...FAILED_DOCUMENT_RESULT_OPTIONS],
                  { placeholder: 'Select result', required: false },
                )}
                {formData.expiryApplies
                  ? renderSelectField('expiredDocumentResult', 'Expired Document Result', EXPIRED_DOCUMENT_RESULT_OPTIONS, {
                      placeholder: 'Select result',
                    })
                  : null}
              </div>
            </div>
          </DashboardMainContainer>
        </div>
        <ConfigurationRuleTestResultPanel testResult={testResultFeedback} />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isAdd ? 'Add Document Rule' : 'Edit Document Rule'}
        onSubmit={handleSave}
        afterForm={(
          <>
            <ConfigurationRuleTestResultPanel testResult={testResultFeedback} />
            <ConfigurationSaveTestModal
              modal={saveTestModal}
              onCancel={() => setSaveTestModal(null)}
              onRunTest={handleRunSaveTest}
              onSaveWithoutTest={handleSaveWithoutTest}
            />
          </>
        )}
      >
        {showRuleId ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        {renderInput('ruleName', 'Rule Name', 'Enter rule name')}
        {renderSelectField('ruleArea', 'Module', DOCUMENT_MODULE_OPTIONS, { placeholder: 'Select module' })}
        {renderSelectField('entryPoint', 'Entry Point', entryPointOptions, {
          placeholder: formData.ruleArea ? 'Select entry point' : 'Select module first',
          disabled: !formData.ruleArea,
        })}
        {errors.form ? <AdminFormFieldError role="alert">{errors.form}</AdminFormFieldError> : null}
        <ToggleRow
          isView={false}
          label="Upload Allowed"
          required
          checked={formData.uploadAllowed}
          onChange={(v) => handleChange('uploadAllowed', v)}
        />
        {formData.uploadAllowed ? (
          <>
            <MultiCheckboxDropdown
              label="Allowed File Formats"
              required
              options={FILE_FORMAT_OPTIONS}
              values={formData.allowedFileFormats}
              onToggle={(v) => toggleFormat(v)}
              error={errors.allowedFileFormats}
              placeholder="Select formats"
            />
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Maximum File Size <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <div className="flex items-center gap-2 w-full min-w-0">
                <input
                  type="number"
                  min={1}
                  placeholder="10"
                  value={formData.maximumFileSize}
                  onChange={(e) => handleChange('maximumFileSize', e.target.value)}
                  className={`${adminFormDetailInputClasses(false, Boolean(errors.maximumFileSize))} flex-1 min-w-0`}
                />
                <span className="text-sm text-offblack shrink-0">MB</span>
              </div>
              {errors.maximumFileSize ? <AdminFormFieldError>{errors.maximumFileSize}</AdminFormFieldError> : null}
            </div>
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Maximum File Count <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <input
                type="number"
                min={1}
                placeholder="1"
                value={formData.maximumFileCount}
                onChange={(e) => handleChange('maximumFileCount', e.target.value)}
                className={adminFormDetailInputClasses(false, Boolean(errors.maximumFileCount))}
              />
              {errors.maximumFileCount ? <AdminFormFieldError>{errors.maximumFileCount}</AdminFormFieldError> : null}
            </div>
            <ToggleRow
              isView={false}
              label="Multiple Upload Allowed"
              required
              checked={formData.multipleUploadAllowed}
              onChange={(v) => handleChange('multipleUploadAllowed', v)}
            />
          </>
        ) : null}
        <ToggleRow isView={false} label="Check Required" required checked={formData.checkRequired} onChange={(v) => handleChange('checkRequired', v)} />
        <ToggleRow isView={false} label="Check Outcome Required" required checked={formData.checkOutcomeRequired} onChange={(v) => handleChange('checkOutcomeRequired', v)} />
        <ToggleRow isView={false} label="More Information Allowed" required checked={formData.moreInformationAllowed} onChange={(v) => handleChange('moreInformationAllowed', v)} />
        <ToggleRow
          isView={false}
          label="Expiry Applies"
          required
          checked={formData.expiryApplies}
          onChange={(v) => {
            setFormData((prev) => ({
              ...prev,
              expiryApplies: v,
              expiryReminderTiming: v ? prev.expiryReminderTiming : '',
              expiredDocumentResult: v ? prev.expiredDocumentResult : '',
            }));
            setErrors((prev) => {
              const n = { ...prev };
              if (!v) {
                delete n.expiryReminderTiming;
                delete n.expiredDocumentResult;
              }
              return n;
            });
          }}
        />
        {formData.expiryApplies
          ? renderSelectField('expiryReminderTiming', 'Expiry Reminder Timing', EXPIRY_REMINDER_TIMING_OPTIONS, {
              placeholder: 'Select timing',
            })
          : null}
        <ToggleRow
          isView={false}
          label="Replacement Allowed"
          required
          checked={formData.replacementAllowed}
          onChange={(v) => {
            setFormData((prev) => ({
              ...prev,
              replacementAllowed: v,
              replacementMethod: v ? prev.replacementMethod : '',
            }));
            if (errors.replacementMethod) {
              setErrors((prev) => {
                const n = { ...prev };
                delete n.replacementMethod;
                return n;
              });
            }
          }}
        />
        {formData.replacementAllowed
          ? renderSelectField('replacementMethod', 'Replacement Rule', REPLACEMENT_RULE_OPTIONS, {
              placeholder: 'Select replacement rule',
            })
          : null}
        {renderSelectField('missingDocumentResult', 'Missing Document Result', MISSING_DOCUMENT_RESULT_OPTIONS, {
          placeholder: 'Select result',
        })}
        {renderSelectField(
          'invalidDocumentResult',
          'Invalid Document Result',
          [{ value: '', label: '—' }, ...INVALID_DOCUMENT_RESULT_OPTIONS],
          { placeholder: 'Select result', required: false },
        )}
        {renderSelectField(
          'failedDocumentResult',
          'Failed Document Result',
          [{ value: '', label: '—' }, ...FAILED_DOCUMENT_RESULT_OPTIONS],
          { placeholder: 'Select result', required: false },
        )}
        {formData.expiryApplies
          ? renderSelectField('expiredDocumentResult', 'Expired Document Result', EXPIRED_DOCUMENT_RESULT_OPTIONS, {
              placeholder: 'Select result',
            })
          : null}
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default DocumentRuleDetail;
